// Toast store — module-level state with subscribers, in the shadcn/ui
// idiom. Single source of truth so toasts queued from a route that
// then unmounts still display in the next route's Toaster.
//
// Usage:
//   const { toast } = useToast();
//   toast({ title: "Report signed", variant: "success" });
//
// Or imperatively from outside React (e.g. axios interceptors):
//   import { toast } from "@/hooks/use-toast";
//   toast({ title: "...", variant: "error" });

import { useEffect, useState } from "react";

const DEFAULT_DURATION_MS = 3_500;
const MAX_TOASTS = 4;

export type ToastVariant = "default" | "success" | "error";

export interface ToastInput {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Override the default 3.5 s duration. ``null`` keeps the toast
   * sticky until ``dismiss(id)`` is called. */
  duration?: number | null;
}

export interface ToastRecord extends ToastInput {
  id: string;
  variant: ToastVariant;
  open: boolean;
}

interface ToastState {
  toasts: ToastRecord[];
}

type Listener = (state: ToastState) => void;

let memoryState: ToastState = { toasts: [] };
const listeners: Listener[] = [];
const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const l of listeners) l(memoryState);
}

function setState(updater: (prev: ToastState) => ToastState): void {
  memoryState = updater(memoryState);
  emit();
}

function genId(): string {
  // No need for crypto-grade randomness; collision risk on 4 active
  // toasts is essentially zero.
  return Math.random().toString(36).slice(2, 10);
}

function scheduleDismiss(id: string, ms: number): void {
  const existing = dismissTimers.get(id);
  if (existing) clearTimeout(existing);
  const handle = setTimeout(() => {
    dismissTimers.delete(id);
    dismiss(id);
  }, ms);
  dismissTimers.set(id, handle);
}

export function toast(input: ToastInput): string {
  const id = genId();
  const variant: ToastVariant = input.variant ?? "default";
  // Build conditionally so optional keys stay omitted, not undefined —
  // matches ``exactOptionalPropertyTypes`` semantics.
  const record: ToastRecord = {
    id,
    title: input.title,
    variant,
    open: true,
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.duration !== undefined ? { duration: input.duration } : {}),
  };

  setState((prev) => {
    // Trim oldest if we're over the cap.
    const queue = [...prev.toasts, record];
    while (queue.length > MAX_TOASTS) queue.shift();
    return { toasts: queue };
  });

  // null duration = sticky, undefined = use default.
  if (input.duration !== null) {
    scheduleDismiss(id, input.duration ?? DEFAULT_DURATION_MS);
  }

  return id;
}

export function dismiss(id: string): void {
  // Animate out: flip ``open`` to false; the Radix component fires
  // ``onOpenChange`` after its exit transition, at which point we drop
  // the record from state.
  setState((prev) => ({
    toasts: prev.toasts.map((t) => (t.id === id ? { ...t, open: false } : t)),
  }));
  const existing = dismissTimers.get(id);
  if (existing) clearTimeout(existing);
  dismissTimers.delete(id);
}

export function removeToast(id: string): void {
  setState((prev) => ({
    toasts: prev.toasts.filter((t) => t.id !== id),
  }));
}

export function useToast(): {
  toasts: ToastRecord[];
  toast: typeof toast;
  dismiss: typeof dismiss;
} {
  const [state, setLocalState] = useState<ToastState>(memoryState);
  useEffect(() => {
    const listener: Listener = (s) => setLocalState(s);
    listeners.push(listener);
    return () => {
      const idx = listeners.indexOf(listener);
      if (idx !== -1) listeners.splice(idx, 1);
    };
  }, []);
  return { toasts: state.toasts, toast, dismiss };
}
